import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { conversationService, type Message } from '@/services';

export function useMessages(conversationId: string | null, uid: string | null) {
  const queryClient = useQueryClient();
  const [retryKey, setRetryKey] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  // Query for messages data (filled by subscription below)
  const { data } = useQuery<Message[]>({
    queryKey: ['messages', conversationId],
    // placeholder only; subscription fills the cache
    queryFn: () => [],
    enabled: false,
    staleTime: Infinity,
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!conversationId || !uid) return;

    const unsubscribe = conversationService.subscribeToMessages(
      conversationId,
      uid,
      updatedMessages => {
        setError(null);
        queryClient.setQueryData(['messages', conversationId], updatedMessages);
      },
      err => {
        setError(err);
      }
    );

    return () => unsubscribe();
  }, [conversationId, uid, queryClient, retryKey]);

  const sendMessage = async (message: Omit<Message, 'id'>) => {
    await conversationService.sendMessage(message);
  };

  const refetch = useCallback(() => {
    setError(null);
    setRetryKey(prev => prev + 1);
  }, []);

  return {
    messages: data ?? [],
    isLoading: Boolean(conversationId && uid && data === undefined && !error),
    isError: error != null,
    error,
    refetch,
    sendMessage,
  };
}
